import type { Response } from "@/api";

interface CacheData<T> {
  value: T;
  createdAt: number;
  expiresAt: number;
}

export class CacheService {
  static disableCache = false;
  // set default expiration to 30 minutes
  static expirationTimeInMinutes = 30;

  static getDefaultExpiresAt(
    expirationTimeInMinutes: number | null = null
  ): Date {
    return new Date(
      new Date().getTime() +
      (expirationTimeInMinutes ?? this.expirationTimeInMinutes) * 60000
    );
  }

  static has(key: string): boolean {
    const data = localStorage.getItem(key);
    if (!data) return false;
    const cacheData = JSON.parse(data) as CacheData<unknown>;
    return cacheData && cacheData.expiresAt > new Date().getTime();
  }

  static get<T>(key: string, value?: () => T, expiresAt?: Date): T {
    if (CacheService.disableCache)
      return value ? value() : (null as unknown as T);

    const data = localStorage.getItem(key);
    let result = {} as CacheData<T>;
    if (data) {
      result = JSON.parse(data) as CacheData<T>;
    }
    if (result?.expiresAt > new Date().getTime()) {
      return result.value;
    }

    if (!value) return null as unknown as T;

    const v = value();

    // Try catch in case quota exceeded or storage is disabled
    try {
      localStorage.setItem(
        key,
        JSON.stringify({
          value: v,
          createdAt: new Date().getTime(),
          expiresAt:
            expiresAt?.getTime() || this.getDefaultExpiresAt().getTime(),
        } as CacheData<T>)
      );
    } catch (e) {
      console.warn("CacheService, get(), setItem error", e);
    }
    return v;
  }

  static async getAsync<T>(
    key: string,
    value: () => Promise<T>,
    expiresAt?: Date
  ): Promise<T> {
    if (CacheService.disableCache) return await value();

    const data = localStorage.getItem(key);
    let result = {} as CacheData<T>;
    if (data) {
      result = JSON.parse(data) as CacheData<T>;
    }
    if (result?.expiresAt > new Date().getTime()) {
      return result.value;
    }

    const v = await value();

    // Try catch in case quota exceeded or storage is disabled
    try {
      localStorage.setItem(
        key,
        JSON.stringify({
          value: v,
          createdAt: new Date().getTime(),
          expiresAt:
            expiresAt?.getTime() || this.getDefaultExpiresAt().getTime(),
        } as CacheData<T>)
      );
    } catch (e) {
      console.warn("CacheService, getAsync(), setItem error", e);
    }
    return v;
  }

  static async getResponseAsync<T>(
    key: string,
    value: () => Promise<Response<T>>,
    expiresAt?: Date
  ): Promise<Response<T>> {
    if (CacheService.disableCache) return await value();

    const result = await this.getAsync(key, value, expiresAt);

    // If the result is not successful, remove response from the cache
    if (!result.success) {
      this.remove(key);
    }

    return result;
  }

  static set<T>(key: string, value: T, expiresAt?: Date) {
    try {
      localStorage.setItem(
        key,
        JSON.stringify({
          value: value,
          createdAt: new Date().getTime(),
          expiresAt:
            expiresAt?.getTime() || this.getDefaultExpiresAt().getTime(),
        } as CacheData<T>)
      );
    } catch (e) {
      console.warn("CacheService, set(), setItem error", e);
    }
  }

  static remove(key: string) {
    localStorage.removeItem(key);
  }

  static removeAllExpired() {
    const now = new Date().getTime();
    const lastCleanup = this.get<number>("lastCleanup");

    // If last cleanup was less than an hour ago, skip removal of expired items
    if (lastCleanup && lastCleanup > now - 1000 * 60 * 60)
      return;

    Object.keys(localStorage).forEach((key) => {
      try {
        const data = localStorage.getItem(key);
        if (data) {
          const cacheData = JSON.parse(data) as CacheData<unknown>;
          if (cacheData.expiresAt <= now) {
            localStorage.removeItem(key);
          }
        }
      }
      catch (e) {
        // skip item if it cannot be parsed
      }
    });

    // Update last cleanup time
    this.set("lastCleanup", now, this.getDefaultExpiresAt(60));
  }

  static clear() {
    localStorage.clear();
  }
}
